import { useState } from "react";
import { Table, Tag } from "antd";
import dayjs from "dayjs";

export const statusColors = {
  succeeded: { bg: "#e6fffb", color: "#006d75", border: "#87e8de" },
  requires_capture: { bg: "#fffbe6", color: "#ad8b00", border: "#ffe58f" },
  failed: { bg: "#fff2f0", color: "#cf1322", border: "#ffa39e" },
};

export const getTagStyle = (status) => {
  const colors = statusColors[status];
  return {
    backgroundColor: colors?.bg || "#f5f5f5",
    color: colors?.color || "#000",
    padding: "2px 10px",
    borderRadius: "6px",
    fontSize: "0.65rem",
    border: `1px solid ${colors?.border || "#d9d9d9"}`,
    fontWeight: 600,
    cursor: "default",
  };
};

export default function PayoutChargesTable({
  charges = [],
  loading,
  meta,
  onFetchCharges,
}) {
  const [currentPage, setCurrentPage] = useState(
    meta?.number ? meta.number + 1 : 1,
  );

  const handleTableChange = (pagination) => {
    const { current, pageSize } = pagination;
    setCurrentPage(current);
    if (onFetchCharges) {
      // Convert to 0-indexed for backend
      onFetchCharges({ page: current - 1, size: pageSize });
    }
  };

  const columns = [
    {
      title: "Payer",
      dataIndex: ["payer", "email"],
      key: "payer",
      ellipsis: true,
      render: (_, record) =>
        record.payer
          ? `${record.payer.firstName} ${record.payer.lastName} (${record.payer.email})`
          : "-",
    },
    {
      title: "Payee",
      dataIndex: ["payee", "email"],
      key: "payee",
      ellipsis: true,
      render: (_, record) =>
        record.payee
          ? `${record.payee.firstName} ${record.payee.lastName} (${record.payee.email})`
          : "-",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (v) => (v ? `$${parseFloat(v).toFixed(2)}` : "-"),
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      render: (v) => v?.toUpperCase() || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag style={getTagStyle(status)}>{status?.toUpperCase()}</Tag>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v) => (v ? dayjs(v).format("YYYY-MM-DD HH:mm") : "-"),
    },
  ];

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={charges}
      pagination={{
        current: currentPage,
        pageSize: meta?.size || 10,
        total: meta?.totalElements || charges.length,
      }}
      onChange={handleTableChange}
      loading={loading}
      className="custom-table"
      size="small"
      //   scroll={{ x: 2800 }}
      expandable={{
        expandedRowRender: (record) => (
          <div style={{ paddingLeft: 50 }}>
            <p className="mb-1 pb-0">
              <strong>Charge Ref:</strong> {record.paymentProcessorId}
            </p>
            <p className="mb-1 pb-0">
              <strong>Payer:</strong>{" "}
              {record.payer
                ? `${record.payer.firstName} ${record.payer.lastName} (${record.payer.email})`
                : "-"}
            </p>
            <p className="mb-1 pb-0">
              <strong>Payee:</strong>{" "}
              {record.payee
                ? `${record.payee.firstName} ${record.payee.lastName} (${record.payee.email})`
                : "-"}
            </p>
            <p className="mb-1 pb-0">
              <strong>Amount:</strong> ${parseFloat(record.amount).toFixed(2)}{" "}
              {record.currency?.toUpperCase()}
            </p>
            <p className="mb-1 pb-0">
              <strong>Status:</strong> {record.status?.toUpperCase()}
            </p>
            <p className="mb-1 pb-0">
              <strong>Description:</strong> {record.description || "-"}
            </p>
            <p className="mb-1 pb-0">
              <strong>Notes:</strong> {record.notes || "-"}
            </p>
            <p className="mb-1 pb-0">
              <strong>Payment Status:</strong> {record.paymentStatus || "-"}
            </p>
            <p className="mb-1 pb-0">
              <strong>Payment Method:</strong>{" "}
              {record.paymentMethod?.card?.brand
                ? `${record.paymentMethod.card.brand} **** ${record.paymentMethod.card.last4}`
                : record.paymentMethod?.bankName || "-"}
            </p>
            <p className="mb-1 pb-0">
              <strong>Created At:</strong>{" "}
              {record.createdAt
                ? dayjs(record.createdAt).format("YYYY-MM-DD HH:mm")
                : "-"}
            </p>
            <p className="mb-1 pb-0">
              <strong>Updated At:</strong>{" "}
              {record.updatedAt
                ? dayjs(record.updatedAt).format("YYYY-MM-DD HH:mm")
                : "-"}
            </p>
            <p className="mb-1 pb-0">
              <strong>Raw Stripe Response:</strong>
              <pre style={{ maxHeight: 150, overflow: "auto" }}>
                {JSON.stringify(record.stripePaymentIntent, null, 2)}
              </pre>
            </p>
          </div>
        ),
        rowExpandable: () => true,
      }}
      //   locale={{
      //     emptyText: "No charges found 😕",
      //   }}
    />
  );
}
